import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import Link from 'next/link';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { Reveal } from '../ui/reveal';

/**
 * Karta článku — Figma `60041:3027` (homepage a mriežka blogu) a `60034:2212`
 * (široká karta na vrchu blogu).
 *
 * Rozmery odmerané z Figmy:
 *   mriežka   675×399 · foto a text po polovici · vnútro karty 8, medzera 32
 *             textový stĺpec py 24 pr 24, medzera 60 medzi textom a tlačidlom
 *   široká    1360×496 · foto 656×480 · text zvislo vycentrovaný
 *   mobil     332×427 · foto hore 177 · obsah p 12, medzera 32
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */

const PLACEHOLDER = '/images/placeholder.png';

export function ArticleCard({
  blog,
  featured = false,
  delay = 0,
}: {
  blog: Blog;
  featured?: boolean;
  /** Oneskorenie odhalenia — na postupné nabiehanie kariet v mriežke. */
  delay?: number;
}) {
  const categories = blog.categories?.map(category => category.name).filter(Boolean) ?? [];
  const excerpt = excerptFromHtml(blog.content);
  const href = ROUTES.BLOG_DETAIL({ slug: blog.slug });

  const card = (
    <article
      className={cn(
        'ea-lift group flex min-w-0 flex-1 flex-col gap-2 overflow-hidden rounded-16 bg-[#FFFFFF] p-2',
        'shadow-[0px_0px_90px_0px_rgba(0,0,0,0.08)]',
        'lg:flex-row lg:items-stretch lg:gap-8',
        // Figma: široká karta má pevnú výšku 496 (foto 480 + 2×8 vnútra),
        // nedrží ju obsah. Mriežková sa naopak prispôsobuje textu.
        featured && 'lg:min-h-[496px]',
      )}
    >
      <div className="relative h-[177px] w-full shrink-0 overflow-hidden rounded-16 lg:h-auto lg:min-w-0 lg:flex-1">
        <Image
          src={blog.image?.url || PLACEHOLDER}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          fill
          priority={featured}
          sizes={featured ? '(min-width: 1024px) 660px, 100vw' : '(min-width: 1024px) 320px, 100vw'}
          className="ea-zoom object-cover"
        />
      </div>

      <div
        className={cn(
          'flex min-w-0 flex-col gap-8 p-3',
          'lg:flex-1 lg:gap-[60px] lg:py-6 lg:pl-0 lg:pr-6',
          // Široká karta má text zvislo vycentrovaný, mriežková rozťahaný.
          featured ? 'lg:justify-center' : 'justify-between',
        )}
      >
        <div className="flex flex-col gap-2 lg:gap-4">
          {/* Kategórie · dátum. Keď článok nemá kategóriu, odpadá aj deliaca
              čiarka — inak by riadok začínal osamotenou zvislou linkou. */}
          <div className="flex items-start gap-2 lg:gap-4">
            {categories.length > 0 && (
              <>
                <span className={cn(META, 'truncate')}>{categories.join(', ')}</span>
                <span aria-hidden="true" className="w-px self-stretch bg-[rgba(0,0,0,0.1)]" />
              </>
            )}
            <span className={cn(META, 'whitespace-nowrap')}>{formatDate(blog.created_at)}</span>
          </div>

          <Heading tag="h5" title={blog.name} />

          {!!excerpt && (
            <p
              className={cn(
                'text-[14px] leading-[1.57] tracking-normal text-[#55617A]',
                'sm:text-[15px] md:text-[16px] lg:text-[17px] lg:leading-[1.5] xl:text-[18px] xl:leading-[1.44]',
              )}
            >
              {excerpt}
            </p>
          )}
        </div>

        <Button
          asChild
          variant="brand-secondary"
          size="brand"
          rounded="pill"
          className="w-full lg:w-fit"
        >
          <Link href={href}>Prečítať článok</Link>
        </Button>
      </div>
    </article>
  );

  // The featured card is above the fold: never hide its SSR content behind IO.
  return featured ? (
    card
  ) : (
    <Reveal asChild delay={delay}>
      {card}
    </Reveal>
  );
}

/** Meta riadok (kategórie · dátum) — Figma Body/L 18 na desktope, 12 na mobile. */
const META = cn(
  'text-[12px] leading-[1.83] tracking-normal text-[#55617A]',
  'sm:text-[13px] sm:leading-[1.7] md:text-[14px] md:leading-[1.6]',
  'lg:text-[16px] lg:leading-[1.5] xl:text-[18px] xl:leading-[1.44]',
);

/** `2026-08-22T19:56:27.000000Z` → `22.08.2026` (formát z Figmy). */
export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Perex z obsahu článku.
 *
 * Blog v administrácii nemá vlastné pole na perex — vracia sa len `content`
 * ako HTML. Text sa preto vyberá z neho: odstránia sa značky, zlepia biele
 * znaky a oreže sa na celom slove.
 */
export function excerptFromHtml(html: string, limit = 120): string {
  if (!html) return '';
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:]$/, '')}…`;
}
